/**
 * Shared chart primitives for the Finance reports + dashboard (FIN-13).
 *
 * This is the ONLY place in the web app that touches raw Recharts. Screens
 * import the typed wrappers below and never configure Recharts colors, axes,
 * grids or tooltips themselves — so dark mode, density and accent variants all
 * "just work" because every color resolves from a `@k-os/ui` design token.
 *
 * How token colors reach Recharts
 * --------------------------------
 * Recharts wants concrete color strings (it can't consume `var(--token)` in
 * every prop — `fill`/`stroke` on SVG accept it, but legends, tooltips and the
 * active-dot rendering paths don't reliably). So `useChartTheme()` reads the
 * computed values of the relevant CSS custom properties off `<html>` once, and
 * re-reads them whenever the `data-theme` / `data-accent` attributes change
 * (via a MutationObserver). The resolved hex/rgb strings are the single source
 * the chart components draw from — never a literal hex in this file.
 *
 * Recharts itself is a dependency of `apps/web` ONLY (never `packages/*`):
 * Health's hand-rolled SVG sparklines don't cover pies / stacked bars, so the
 * Finance design (§10) sanctions adding it here.
 */

import { useEffect, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { TooltipProps } from 'recharts';
import { formatEur } from '../../screens/finance/finance-format';
import type { ReactNode } from 'react';

// ─── Token → CSS-variable resolution ──────────────────────────────────────────

/**
 * The token names this module consumes. Values are CSS custom-property names
 * declared in `@k-os/ui` `tokens.css`. We resolve them at runtime so theme /
 * accent switches are reflected without a rebuild.
 */
const TOKEN_VARS = {
  ink: '--ink',
  ink2: '--ink-2',
  ink3: '--ink-3',
  ink4: '--ink-4',
  line: '--line',
  line2: '--line-2',
  canvas: '--canvas',
  surface2: '--surface-2',
  accent: '--accent',
  accentInk: '--accent-ink',
  // Status palette — the categorical chart series ramp draws from these so the
  // colors stay coherent with the rest of the product (no bespoke chart palette).
  statusNext: '--status-next',
  statusWaiting: '--status-waiting',
  statusDelegated: '--status-delegated',
  statusBlocked: '--status-blocked',
  statusDone: '--status-done',
  statusSomeday: '--status-someday',
} as const;

export type ChartTheme = Record<keyof typeof TOKEN_VARS, string> & {
  /** Ordered categorical palette for pies / multi-series, drawn from tokens. */
  series: string[];
  /** Semantic money colors. */
  positive: string;
  negative: string;
  net: string;
};

function readTheme(): ChartTheme {
  // SSR / no-DOM guard — falls back to empty strings (charts render blank, not crash).
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    const empty = Object.fromEntries(
      Object.keys(TOKEN_VARS).map((k) => [k, '']),
    ) as Record<keyof typeof TOKEN_VARS, string>;
    return { ...empty, series: [], positive: '', negative: '', net: '' };
  }
  const cs = getComputedStyle(document.documentElement);
  const resolved = Object.fromEntries(
    Object.entries(TOKEN_VARS).map(([key, varName]) => [key, cs.getPropertyValue(varName).trim()]),
  ) as Record<keyof typeof TOKEN_VARS, string>;

  return {
    ...resolved,
    // Categorical ramp: accent first (the product's primary), then the muted
    // status hues. Used for category pies and category-trend lines.
    series: [
      resolved.accent,
      resolved.statusNext,
      resolved.statusWaiting,
      resolved.statusDelegated,
      resolved.statusBlocked,
      resolved.statusDone,
      resolved.statusSomeday,
    ],
    positive: resolved.statusDone, // money in / under-budget green
    negative: resolved.statusBlocked, // money out / over-budget red
    net: resolved.accent,
  };
}

/**
 * Resolve `@k-os/ui` token colors to concrete strings, re-resolving on
 * theme / accent / density attribute changes. Density doesn't carry colors but
 * is cheap to observe and keeps us future-proof.
 */
export function useChartTheme(): ChartTheme {
  const [theme, setTheme] = useState<ChartTheme>(() => readTheme());

  useEffect(() => {
    const root = document.documentElement;
    const update = () => setTheme(readTheme());
    // Re-read once on mount (covers fonts/vars settling after first paint).
    update();
    const observer = new MutationObserver(update);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-theme', 'data-accent', 'data-density'],
    });
    return () => observer.disconnect();
  }, []);

  return theme;
}

// ─── Shared formatting + presentation primitives ──────────────────────────────

const CHART_HEIGHT = 280;
const MINI_CHART_HEIGHT = 160;

/** Compact EUR for axis ticks: €1.2k, €3.4M — keeps the y-axis legible. */
function compactEur(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `€${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `€${(value / 1_000).toFixed(1)}k`;
  return `€${Math.round(value)}`;
}

/** "2026-03" → "Mar" / "Mar '26" (short axis labels). */
export function shortMonth(ym: string, withYear = false): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  const name = new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'short' });
  return withYear ? `${name} '${String(y).slice(2)}` : name;
}

/** ISO week-start date "2026-03-09" → "9 Mar" (compact x-axis label). */
export function shortWeek(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const month = new Date(y, m - 1, d).toLocaleString('en-US', { month: 'short' });
  return `${d} ${month}`;
}

/** The fields we read off each Recharts tooltip payload entry. */
interface TooltipEntry {
  name: string | number | undefined;
  value: number | string | Array<number | string> | undefined;
  color: string | undefined;
}

/**
 * Shared tooltip — token-styled card, EUR-formatted values. Used by every
 * chart so the hover affordance is identical across reports.
 *
 * `renderTooltip(theme, labelFormatter)` below adapts this to the shape
 * Recharts' `content` render-prop expects; this component itself takes only the
 * narrowed fields we render, so the rest of the file never touches Recharts'
 * internal prop names.
 */
function ChartTooltip({
  active,
  payload,
  label,
  theme,
  labelFormatter,
  valueFormatter,
}: {
  active: boolean;
  payload: TooltipEntry[];
  label: string | undefined;
  theme: ChartTheme;
  labelFormatter: ((label: string) => string) | undefined;
  /** Overrides the default EUR formatting (e.g. a percentage series). */
  valueFormatter: ((value: number) => string) | undefined;
}) {
  if (!active || payload.length === 0) return null;
  const labelText = label;
  return (
    <div
      style={{
        background: theme.canvas,
        border: `1px solid ${theme.line}`,
        borderRadius: 8,
        padding: '8px 10px',
        fontSize: 13,
        boxShadow: 'var(--shadow-md)',
        minWidth: 120,
      }}
    >
      {labelText !== undefined && (
        <div style={{ color: theme.ink3, marginBottom: 6, fontSize: 12 }}>
          {labelFormatter ? labelFormatter(labelText) : labelText}
        </div>
      )}
      {payload.map((entry, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 14,
            color: theme.ink,
            lineHeight: 1.7,
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 9,
                height: 9,
                borderRadius: 2,
                background: entry.color ?? theme.ink3,
                display: 'inline-block',
              }}
            />
            {entry.name}
          </span>
          <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {typeof entry.value === 'number'
              ? (valueFormatter ?? formatEur)(entry.value)
              : String(entry.value ?? '')}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Adapt {@link ChartTooltip} to Recharts' `content` render-prop. Recharts hands
 * it the full, loosely-typed tooltip props (readonly payload, `label` as
 * `unknown`); we narrow them here once so the `content={...}` call sites stay
 * clean and type-safe under `exactOptionalPropertyTypes`.
 */
function renderTooltip(
  theme: ChartTheme,
  labelFormatter?: (label: string) => string,
  valueFormatter?: (value: number) => string,
): (props: TooltipProps<number | string, string>) => React.ReactElement | null {
  return ({ active, payload, label }) => (
    <ChartTooltip
      active={active ?? false}
      payload={
        payload
          ? payload.map((p) => ({
              name: p.name,
              value: p.value,
              color: p.color,
            }))
          : []
      }
      label={label == null ? undefined : String(label)}
      theme={theme}
      labelFormatter={labelFormatter}
      valueFormatter={valueFormatter}
    />
  );
}

/** Common axis / grid props so every chart matches. */
function axisProps(theme: ChartTheme) {
  return {
    tick: { fill: theme.ink3, fontSize: 12 },
    stroke: theme.line,
    tickLine: false,
  } as const;
}

const legendStyle = (theme: ChartTheme) => ({ fontSize: 13, color: theme.ink2 });

// ─── Public chart series typing ───────────────────────────────────────────────

export interface BarSeries {
  key: string;
  name: string;
  /** Semantic color from the theme; defaults to the accent. */
  color: string;
  /** For stacking — bars sharing a stackId stack. */
  stackId?: string;
}

export interface LineSeries {
  key: string;
  name: string;
  color: string;
}

export interface AreaSeries {
  key: string;
  name: string;
  color: string;
  stackId?: string;
}

// ─── Grouped / stacked bar chart ──────────────────────────────────────────────

export function FinanceBarChart<T extends object>({
  data,
  xKey,
  bars,
  lines = [],
  height = CHART_HEIGHT,
  xTickFormatter,
}: {
  data: T[];
  xKey: keyof T & string;
  bars: BarSeries[];
  /** Optional overlaid line series (e.g. a net line over in/out bars). */
  lines?: LineSeries[];
  height?: number;
  xTickFormatter?: (value: string) => string;
}) {
  const theme = useChartTheme();
  // Recharts ComposedChart would be ideal, but BarChart accepts <Line> children
  // and renders them as an overlay — simpler and sufficient for our reports.
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={theme.line2} vertical={false} />
        <XAxis
          dataKey={xKey}
          {...(xTickFormatter ? { tickFormatter: xTickFormatter } : {})}
          {...axisProps(theme)}
        />
        <YAxis tickFormatter={compactEur} width={56} {...axisProps(theme)} />
        <Tooltip
          cursor={{ fill: theme.surface2, opacity: 0.5 }}
          content={renderTooltip(theme, xTickFormatter)}
        />
        <Legend wrapperStyle={legendStyle(theme)} />
        {bars.map((b) => (
          <Bar
            key={b.key}
            dataKey={b.key}
            name={b.name}
            fill={b.color}
            maxBarSize={48}
            // Omit stackId / radius entirely when not stacking — passing
            // `undefined` explicitly trips exactOptionalPropertyTypes.
            {...(b.stackId ? { stackId: b.stackId } : { radius: [3, 3, 0, 0] as const })}
          />
        ))}
        {lines.map((l) => (
          <Line
            key={l.key}
            type="monotone"
            dataKey={l.key}
            name={l.name}
            stroke={l.color}
            strokeWidth={2}
            dot={false}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Multi-line chart ─────────────────────────────────────────────────────────

export function FinanceLineChart<T extends object>({
  data,
  xKey,
  lines,
  height = CHART_HEIGHT,
  xTickFormatter,
  yTickFormatter,
  valueFormatter,
}: {
  data: T[];
  xKey: keyof T & string;
  lines: LineSeries[];
  height?: number;
  xTickFormatter?: (value: string) => string;
  /** Y-axis tick text. Defaults to compact EUR. */
  yTickFormatter?: (value: number) => string;
  /** Tooltip value text. Defaults to EUR. Pair with `yTickFormatter` when the
   *  series isn't money (e.g. a savings-rate percentage). */
  valueFormatter?: (value: number) => string;
}) {
  const theme = useChartTheme();
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={theme.line2} vertical={false} />
        <XAxis
          dataKey={xKey}
          {...(xTickFormatter ? { tickFormatter: xTickFormatter } : {})}
          {...axisProps(theme)}
        />
        <YAxis
          tickFormatter={yTickFormatter ?? compactEur}
          width={56}
          {...axisProps(theme)}
        />
        <Tooltip content={renderTooltip(theme, xTickFormatter, valueFormatter)} />
        <Legend wrapperStyle={legendStyle(theme)} />
        {lines.map((l) => (
          <Line
            key={l.key}
            type="monotone"
            dataKey={l.key}
            name={l.name}
            stroke={l.color}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── Stacked area + optional net line ─────────────────────────────────────────

export function FinanceAreaChart<T extends object>({
  data,
  xKey,
  areas,
  lines = [],
  height = CHART_HEIGHT,
  xTickFormatter,
}: {
  data: T[];
  xKey: keyof T & string;
  areas: AreaSeries[];
  lines?: LineSeries[];
  height?: number;
  xTickFormatter?: (value: string) => string;
}) {
  const theme = useChartTheme();
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={theme.line2} vertical={false} />
        <XAxis
          dataKey={xKey}
          {...(xTickFormatter ? { tickFormatter: xTickFormatter } : {})}
          {...axisProps(theme)}
        />
        <YAxis tickFormatter={compactEur} width={56} {...axisProps(theme)} />
        <Tooltip content={renderTooltip(theme, xTickFormatter)} />
        <Legend wrapperStyle={legendStyle(theme)} />
        {areas.map((a) => (
          <Area
            key={a.key}
            type="monotone"
            dataKey={a.key}
            name={a.name}
            stroke={a.color}
            fill={a.color}
            fillOpacity={0.18}
            strokeWidth={1.5}
            // Omit when undefined — exactOptionalPropertyTypes.
            {...(a.stackId ? { stackId: a.stackId } : {})}
          />
        ))}
        {lines.map((l) => (
          <Line
            key={l.key}
            type="monotone"
            dataKey={l.key}
            name={l.name}
            stroke={l.color}
            strokeWidth={2}
            dot={false}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ─── Pie ──────────────────────────────────────────────────────────────────────

export interface PieDatum {
  name: string;
  value: number;
}

/**
 * Donut centre text renderer. Recharts hands `<Label content>` the label props
 * including the pie's `viewBox` (`cx`/`cy`/radii) — the only reliable way to
 * anchor text to the hole, since a bottom legend shifts the pie off the
 * container's own centre. Typed with a `viewBox: unknown` supertype so it stays
 * assignable to Recharts' `ContentType` under `strictFunctionTypes`.
 */
function pieCenterLabel(theme: ChartTheme, label: string, value: string) {
  return (props: { viewBox?: unknown }) => {
    const vb = props.viewBox as { cx?: number; cy?: number } | undefined;
    if (!vb || typeof vb.cx !== 'number' || typeof vb.cy !== 'number') return null;
    return (
      <g>
        <text
          x={vb.cx}
          y={vb.cy - 11}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={theme.ink3}
          fontSize={11}
          fontWeight={600}
          letterSpacing="0.06em"
        >
          {label.toUpperCase()}
        </text>
        <text
          x={vb.cx}
          y={vb.cy + 9}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={theme.ink}
          fontSize={15}
          fontWeight={700}
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {value}
        </text>
      </g>
    );
  };
}

export function FinancePieChart({
  data,
  height = CHART_HEIGHT,
  centerLabel,
  centerValue,
}: {
  data: PieDatum[];
  height?: number;
  /** Small caps caption above {@link centerValue} in the donut hole. */
  centerLabel?: string;
  /** Pre-formatted total shown in the donut hole (widens the hole to fit). */
  centerValue?: string;
}) {
  const theme = useChartTheme();
  const palette = theme.series.filter(Boolean);
  const hasCenter = centerValue !== undefined;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Tooltip content={renderTooltip(theme)} />
        <Legend wrapperStyle={legendStyle(theme)} />
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={hasCenter ? '58%' : '45%'}
          outerRadius="75%"
          paddingAngle={1}
          stroke={theme.canvas}
          strokeWidth={1}
        >
          {data.map((_, i) => (
            <Cell key={i} fill={palette[i % palette.length] || theme.accent} />
          ))}
          {hasCenter && (
            <Label
              position="center"
              content={pieCenterLabel(theme, centerLabel ?? 'Total', centerValue)}
            />
          )}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
}

// ─── Candlestick (weekly balance OHLC) ────────────────────────────────────────
//
// Recharts has no native candlestick. We render one on top of a hidden `<Bar>`
// whose `dataKey` returns the `[low, high]` range for each datum: Recharts then
// lays the bar out spanning low→high, and hands our custom `shape` the pixel
// rect (`x, y, width, height`) for that range plus the datum's `payload`. From
// that rect we derive a value→pixel scale and draw (a) a thin wick from low to
// high and (b) a thicker body between open and close — green when close ≥ open,
// red otherwise. All colors come from the theme tokens (never a literal hex).

/** One OHLC datum. `x` is the categorical key (e.g. ISO week-start). */
export interface CandleDatum {
  /** Categorical x value (string key plotted on the x-axis). */
  x: string;
  open: number;
  close: number;
  high: number;
  low: number;
  /** Carried through to the tooltip for per-row detail. */
  [extra: string]: unknown;
}

/** Props Recharts hands a `<Bar shape>` render-prop (the fields we use). */
interface CandleShapeProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: CandleDatum;
  // Injected by us via the Bar's extra props (Recharts forwards unknown props).
  positive?: string;
  negative?: string;
}

/**
 * Custom candle shape. The incoming rect (`y`, `height`) spans the bar's value
 * range — i.e. `[low, high]` — so `height / (high - low)` is the px-per-EUR
 * scale we use to position the wick and the open/close body within it.
 */
function CandleShape(props: CandleShapeProps) {
  const { x, y, width, height, payload, positive, negative } = props;
  if (
    x == null ||
    y == null ||
    width == null ||
    height == null ||
    !payload ||
    width <= 0
  ) {
    return null;
  }

  const { open, close, high, low } = payload;
  const span = high - low;
  // Degenerate (flat) week: draw a 1px line across the bar's width.
  const scale = span > 0 ? height / span : 0;
  const yFor = (v: number) => y + (high - v) * scale;

  const up = close >= open;
  const color = up ? positive : negative;
  const cx = x + width / 2;
  const bodyW = Math.max(width * 0.6, 3);
  const bodyX = cx - bodyW / 2;

  const openY = yFor(open);
  const closeY = yFor(close);
  const bodyTop = Math.min(openY, closeY);
  const bodyH = Math.max(Math.abs(closeY - openY), 2); // min 2px so a flat body is visible

  return (
    <g>
      {/* Wick: low → high */}
      <line
        x1={cx}
        x2={cx}
        y1={y}
        y2={y + height}
        stroke={color}
        strokeWidth={1.5}
      />
      {/* Body: open ↔ close */}
      <rect
        x={bodyX}
        y={bodyTop}
        width={bodyW}
        height={bodyH}
        fill={color}
        rx={1}
      />
    </g>
  );
}

/**
 * Candlestick chart for weekly balance OHLC. Pass `tooltip` to render a custom
 * hover card (the dashboard supplies the per-account breakdown); otherwise the
 * shared OHLC tooltip is used.
 */
export function FinanceCandlestickChart({
  data,
  height = CHART_HEIGHT,
  xTickFormatter,
  tooltip,
}: {
  data: CandleDatum[];
  height?: number;
  xTickFormatter?: (value: string) => string;
  /** Custom tooltip render-prop; receives the active datum (or null). */
  tooltip?: (datum: CandleDatum | null) => ReactNode;
}) {
  const theme = useChartTheme();

  // Y-axis must cover every low/high across the dataset (the per-bar range
  // alone wouldn't give a shared scale), with a little headroom.
  const lows = data.map((d) => d.low);
  const highs = data.map((d) => d.high);
  const min = lows.length ? Math.min(...lows) : 0;
  const max = highs.length ? Math.max(...highs) : 0;
  const pad = Math.max((max - min) * 0.08, 1);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={theme.line2} vertical={false} />
        <XAxis
          dataKey="x"
          {...(xTickFormatter ? { tickFormatter: xTickFormatter } : {})}
          {...axisProps(theme)}
        />
        <YAxis
          tickFormatter={compactEur}
          width={56}
          domain={[min - pad, max + pad]}
          {...axisProps(theme)}
        />
        <Tooltip
          cursor={{ fill: theme.surface2, opacity: 0.5 }}
          content={(rcProps: TooltipProps<number | string, string>) => {
            const active = rcProps.active ?? false;
            const datum =
              active && rcProps.payload && rcProps.payload.length > 0
                ? (rcProps.payload[0]?.payload as CandleDatum | undefined) ?? null
                : null;
            if (tooltip) return tooltip(datum);
            return <CandlestickOhlcTooltip datum={datum} theme={theme} />;
          }}
        />
        <Bar
          dataKey={(d: CandleDatum) => [d.low, d.high]}
          isAnimationActive={false}
          // Recharts forwards unknown props to the shape — pass the resolved
          // token colors so the shape stays hex-free.
          shape={
            <CandleShape positive={theme.positive} negative={theme.negative} />
          }
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Default OHLC tooltip — token-styled, EUR-formatted. */
function CandlestickOhlcTooltip({
  datum,
  theme,
}: {
  datum: CandleDatum | null;
  theme: ChartTheme;
}) {
  if (!datum) return null;
  const rows: Array<[string, number]> = [
    ['Open', datum.open],
    ['Close', datum.close],
    ['High', datum.high],
    ['Low', datum.low],
  ];
  return (
    <div
      style={{
        background: theme.canvas,
        border: `1px solid ${theme.line}`,
        borderRadius: 8,
        padding: '8px 10px',
        fontSize: 13,
        boxShadow: 'var(--shadow-md)',
        minWidth: 140,
      }}
    >
      {rows.map(([k, v]) => (
        <div
          key={k}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 14,
            color: theme.ink,
            lineHeight: 1.7,
          }}
        >
          <span style={{ color: theme.ink3 }}>{k}</span>
          <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {formatEur(v)}
          </span>
        </div>
      ))}
    </div>
  );
}

export { CHART_HEIGHT, MINI_CHART_HEIGHT };
